import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdminUsers() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [togglingUser, setTogglingUser] = useState(null);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(API_URL + '/api/admin/users', {
        headers: { Authorization: 'Bearer ' + token }
      });
      setUsers(response.data.users || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [token]);

  const handleSyncStripe = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const response = await axios.post(API_URL + '/api/admin/sync-stripe', {}, {
        headers: { Authorization: 'Bearer ' + token }
      });
      setSyncResult(response.data);
      fetchUsers();
    } catch (err) {
      setSyncResult({ status: 'error', message: err.response?.data?.detail || 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleSubscription = async (userId, currentActive) => {
    setTogglingUser(userId);
    try {
      await axios.put(
        API_URL + '/api/admin/users/' + userId + '/subscription?active=' + (!currentActive),
        {},
        { headers: { Authorization: 'Bearer ' + token } }
      );
      fetchUsers();
    } catch (err) {
      alert('Failed to update: ' + (err.response?.data?.detail || err.message));
    } finally {
      setTogglingUser(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      <div className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={function(){ navigate('/admin'); }} className="text-slate-400 hover:text-white" data-testid="back-btn">
                Back
              </button>
              <h1 className="text-2xl font-bold text-white">User Management</h1>
            </div>
            <button
              onClick={handleSyncStripe}
              disabled={syncing}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-800 text-white text-sm rounded-lg transition-colors"
              data-testid="sync-stripe-btn"
            >
              {syncing ? 'Syncing with Stripe...' : 'Sync with Stripe'}
            </button>
          </div>
        </div>
      </div>

      {syncResult && (
        <div className="max-w-7xl mx-auto px-4 pt-4">
          <div className={`rounded-lg p-4 text-sm ${syncResult.status === 'success' ? 'bg-green-900/50 border border-green-700 text-green-300' : 'bg-red-900/50 border border-red-700 text-red-300'}`} data-testid="sync-result">
            {syncResult.status === 'success' ? (
              <div>
                <p className="font-semibold">Stripe sync complete</p>
                <p>Deactivated: {syncResult.deactivated_count} user(s)</p>
                {syncResult.deactivated_users?.length > 0 && (
                  <p>Users deactivated: {syncResult.deactivated_users.join(', ')}</p>
                )}
                <p>Already correct: {syncResult.already_correct}</p>
              </div>
            ) : (
              <p>{syncResult.message || 'Sync failed'}</p>
            )}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left px-6 py-4 text-slate-400 text-sm">User</th>
                <th className="text-left px-6 py-4 text-slate-400 text-sm">Status</th>
                <th className="text-left px-6 py-4 text-slate-400 text-sm">Joined</th>
                <th className="text-left px-6 py-4 text-slate-400 text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(function(user) {
                return (
                  <tr key={user.id} className="border-b border-slate-700/50">
                    <td className="px-6 py-4">
                      <p className="text-white">{user.full_name || 'No name'}</p>
                      <p className="text-slate-400 text-sm">{user.email}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={user.subscription_active ? 'text-green-400' : 'text-slate-400'} data-testid={'user-status-' + user.id}>
                        {user.subscription_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-400 text-sm">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      {!user.is_admin && (
                        <button
                          onClick={() => handleToggleSubscription(user.id, user.subscription_active)}
                          disabled={togglingUser === user.id}
                          className={`px-3 py-1 text-xs rounded-lg transition-colors ${
                            user.subscription_active
                              ? 'bg-red-600/20 text-red-400 hover:bg-red-600/40 border border-red-700'
                              : 'bg-green-600/20 text-green-400 hover:bg-green-600/40 border border-green-700'
                          } disabled:opacity-50`}
                          data-testid={'toggle-sub-' + user.id}
                        >
                          {togglingUser === user.id ? '...' : user.subscription_active ? 'Deactivate' : 'Activate'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AdminUsers;
